import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import '../components/RealEstatePage.css';
import './CreateEditListing.js';

const RealEstatePage = () => {
    const { isLoggedIn } = useAuth();
    const navigate = useNavigate();
    const [selectedListing, setSelectedListing] = useState(null);
    const [listings, setListings] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImageIndex, setModalImageIndex] = useState(0);
    const selectedListingRef = useRef(null);

    useEffect(() => {
        axios.get('https://amywagnerdesigns.com/api/real-estate-listings')
            .then(response => {
                const updatedListings = response.data.map(listing => {
                    listing.images = listing.images.map(image => `https://amywagnerdesigns.com/uploads/${image}`);
                    return listing;
                });

                // 🔽 Sort by status priority, then by newest first (highest ID)
                const statusPriority = {
                    'for sale': 1,
                    'under contract': 2,
                    'pending sale': 3,
                    'sold': 4
                };

                updatedListings.sort((a, b) => {
                    const priorityA = statusPriority[a.status.toLowerCase()] || 99;
                    const priorityB = statusPriority[b.status.toLowerCase()] || 99;

                    if (priorityA === priorityB) {
                        return b.id - a.id; // Newest first within same status
                    }

                    return priorityA - priorityB;
                });

                setListings(updatedListings);
                if (updatedListings.length > 0) {
                    setSelectedListing(updatedListings[0]);
                }
            })
            .catch(error => console.error("Error fetching listings:", error));
    }, []);

    const openImageModal = (index) => {
        setModalImageIndex(index);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleNextImage = () => {
        if (selectedListing) {
            setModalImageIndex((prevIndex) =>
                (prevIndex + 1) % selectedListing.images.length
            );
        }
    };

    const handlePrevImage = () => {
        if (selectedListing) {
            setModalImageIndex((prevIndex) =>
                (prevIndex - 1 + selectedListing.images.length) %
                selectedListing.images.length
            );
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isModalOpen) {
                if (e.key === 'Escape') closeModal(); // ⬅️ ESC key closes modal
                if (e.key === 'ArrowRight') handleNextImage();
                if (e.key === 'ArrowLeft') handlePrevImage();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, modalImageIndex]);


    const handleCreateListing = () => {
        navigate('/real-estate/new');
    };

    const handleEditListing = () => {
        if (selectedListing) {
            navigate(`/real-estate/edit/${selectedListing.id}`);
        }
    };

    const handleSelectListing = (listing) => {
        setSelectedListing(listing);
        setTimeout(() => {
            selectedListingRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
        }, 0);
    };


    const handleDeleteListing = async () => {
        if (!selectedListing) return;

        try {
            await axios.delete(`https://amywagnerdesigns.com/api/real-estate-listings/${selectedListing.id}`);
            const updatedListings = listings.filter(listing => listing.id !== selectedListing.id);
            setListings(updatedListings);
            setSelectedListing(updatedListings.length > 0 ? updatedListings[0] : null);
        } catch (error) {
            console.error("Error deleting listing:", error);
        }
    };

    const ListingThumbnail = ({ listing, onSelect, isSelected }) => {
        const imageUrl = listing.images?.[0] || 'https://amywagnerdesigns.com/uploads/Gant01.jpeg';
        return (
            <div className={`listing-thumbnail ${isSelected ? 'selected' : ''}`} onClick={() => onSelect(listing)}>
                <img src={imageUrl} alt="" />
                <div className={`status-banner ${listing.status.replace(" ", "-").toLowerCase()}`}>
                    {listing.status.replace("-", " ")}
                </div>
                <div className="thumbnail-info">
                    <h3>${listing.price}</h3>
                    <p>
                        {listing.address}<br />
                        {listing.city}, {listing.state} {listing.zip}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="real-estate-page">
                <div className="real-estate-banner">Real Estate Listings</div>

                {selectedListing && (
                    <div className="selected-listing" ref={selectedListingRef}>
                        <div className={`selected-status ${selectedListing.status.replace(" ", "-").toLowerCase()}`}>
                            {selectedListing.status.replace("-", " ")}
                        </div>

                        <div className="listing-info-row">
                            <div className="listing-info-item">
                                <strong>Price:</strong> ${selectedListing.price}
                            </div>
                            <div className="listing-info-item">
                                <strong>MLS:</strong> {selectedListing.mls}
                            </div>
                            <div className="listing-info-item">
                                <strong>Address:</strong> {selectedListing.address}, {selectedListing.city}, {selectedListing.state} {selectedListing.zip}
                            </div>
                            <div className="listing-info-item">
                                <strong>Beds:</strong> {selectedListing.beds}, <strong>Baths:</strong> {selectedListing.baths}
                            </div>
                            <div className="request-showing-container">
                                <Link to="/contact#contact-form" className="request-showing-btn">
                                    Request a showing!
                                </Link>
                            </div>
                        </div>

                        <div className="listing-images">
                            <img
                                src={selectedListing.images[0]}
                                alt="Featured"
                                className="featured-image"
                                onClick={() => openImageModal(0)}
                            />
                        </div>

                        {/* Description Section */}
                        <div className="listing-details">
                            <strong>Description:</strong>
                            <p>{selectedListing.description}</p>
                        </div>

                        <div className="thumbnail-gallery">
                            {selectedListing.images.map((image, index) => (
                                <div key={index} className="thumbnail-wrapper">
                                    <img
                                        src={image}
                                        alt=""
                                        className="thumbnail-image"
                                        onClick={() => openImageModal(index)}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Admin Controls */}
                        {isLoggedIn && (
                            <div className="listing-actions">
                                <button onClick={handleCreateListing} className="create-listing-btn">
                                    Create New Listing
                                </button>
                                {selectedListing && (
                                    <>
                                        <button onClick={handleEditListing} className="edit-listing-btn">
                                            Edit Listing
                                        </button>
                                        <button onClick={handleDeleteListing} className="delete-listing-btn">
                                            Delete Listing
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Active Listings */}
                <div className="listing-thumbnails">
                    {listings.filter(listing => listing.status !== 'sold').map((listing) => (
                        <ListingThumbnail
                            key={listing.id}
                            listing={listing}
                            isSelected={listing === selectedListing}
                            onSelect={handleSelectListing}
                        />
                    ))}
                </div>

                {/* Sold Listings */}
                {listings.some(listing => listing.status === 'sold') && (
                    <div className="sold-listings">
                        <h2>Sold Properties</h2>
                        <div className="listing-thumbnails">
                            {listings.filter(listing => listing.status === 'sold').map((listing) => (
                                <ListingThumbnail
                                    key={listing.id}
                                    listing={listing}
                                    isSelected={listing === selectedListing}
                                    onSelect={handleSelectListing}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Image Modal */}
                {isModalOpen && selectedListing && (
                    <div
                        className="modal-overlay"
                        onClick={closeModal}
                        onTouchStart={(e) => (window.touchStartX = e.touches[0].clientX)}
                        onTouchEnd={(e) => {
                            const deltaX = e.changedTouches[0].clientX - window.touchStartX;
                            if (deltaX > 50) handlePrevImage();
                            if (deltaX < -50) handleNextImage();
                        }}
                    >
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            {/* Close X Button */}
                            <button className="close-modal-button" onClick={closeModal}>✕</button>

                            {/* Left / Right Arrows */}
                            <button className="arrow-button arrow-left" onClick={handlePrevImage}>❮</button>
                            <img
                                src={selectedListing.images[modalImageIndex]}
                                alt="Large view"
                            />
                            <button className="arrow-button arrow-right" onClick={handleNextImage}>❯</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RealEstatePage;

